import { Component, OnInit, Input } from "@angular/core";
import { Section } from "../../models";
import { AuthorizationService, GlobalFilterService } from "../../services";
import { environment } from "../../../environments/environment";
import Theme from "b2b-theme";
import "b2b-theme/dist/b2b-theme.css";
import * as $ from "jquery";
import * as _ from "lodash";
import { SelectItem } from "primeng/api/selectitem";
import { PhiService } from "../../services/phi.service";
import { filter } from "rxjs/internal/operators/filter";
import { NavigationEnd, Router } from "@angular/router";

declare function initializeHealthcareTopBarV2(
  parentContainer: JQuery,
  auth0ClientId: string,
  auth0Domain: string,
  auth0RefreshToken: boolean,
  auth0Scope: string,
  redirectUri?: string,
  PHILoggingToggleCallback?: (toggleStatus: string) => void
): Promise<any>;
@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.css"],
})
export class HeaderComponent implements OnInit {
  private topBarLoaded = false;

  public displayBanner: boolean = false;
  public showStickyPopup: boolean = false;
  public usersClients: SelectItem[] = [];
  public clientsLoaded: boolean = false;
  public clientName: string;
  public currentClient: number;
  public statusRollup: string;
  public readonly apptitle = environment.applicationDisplayName;
  @Input() sections: string;
  private _phiService: PhiService;

  public sectionsObj: Section[];
  validSections = [];

  constructor(
    private globalFilterService: GlobalFilterService,
    private authorizationService: AuthorizationService,
    private phiService: PhiService,
    private router: Router
  ) {
    this._phiService = phiService;

    // Subscribe to navigation events to reapply banner layout and scroll to top
    // This ensures consistent layout when navigating between routes
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        // Only apply if currentClient is set
        if (this.currentClient) {
          this.applyBannerLayoutOffset();
          window.scrollTo(0, 0); // Ensure we're at the top after navigation
        }
      });

    // Listen for scroll events to show/hide sticky popup
    window.addEventListener('scroll', this.onScroll.bind(this));
  }

  async ngOnInit() {
    this.sectionsObj = JSON.parse(this.sections);
    if (this.topBarLoaded) {
      return;
    }

    try {
      await this.injectHealthcareTopBar();

      let topBar = await initializeHealthcareTopBarV2(
        $("#healthcareTopBar"),
        environment.auth0.clientId,
        environment.auth0.domain,
        environment.auth0.refreshToken,
        environment.auth0.scope,
        null,
        this.PHIToggleCallback.bind(this)
      );

      await topBar.initialize();
      window["healthcareTopBar"] = topBar;
      this.authorizationService.setHealthcareTopBar(topBar);
      await this.authorizationService.waitUntilReady();

      Theme.init();
      this.topBarLoaded = true;
      this.setValidSections();

      this.globalFilterService.ClientsLoaded.subscribe((response) => {
        _.forEach(this.globalFilterService.clients, (client) => {
          this.usersClients.push({
            label: `${client.ClientId} - ${client.ClientName}`,
            value: client.ClientId,
          });
        });
        this.clientsLoaded = true;

        // Set initial current client and apply banner layout if needed
        // Try to restore client from local storage, otherwise default to first client
        // Apply custom banner layout for specific clients (3010, 3020)
        if (this.usersClients.length > 0) {
          var sessionClientId = localStorage.getItem(this.globalFilterService.CLIENTID);
          if (sessionClientId) {
            this.globalFilterService.selectedClientId = sessionClientId;
          } else {
            this.globalFilterService.selectedClientId = this.usersClients[0].value;
          }
          this.currentClient = parseInt(this.globalFilterService.selectedClientId);

          if (this.currentClient === 3010 || this.currentClient === 3020) {
            this.applyBannerLayoutOffset();
          }
        }
      });
      this.globalFilterService.loadClients();
      this.authorizationService.HealthcareTopBar.notifyActivity();
    } catch (error) {
      console.log(error);
    }
  }

  private setValidSections(): void {
    this.validSections = this.sectionsObj.filter(
      (section) =>
        !section.subSection &&
        this.authorizationService.hasFeature(section.feature, true)
    );
  }

  clientSelected(event: any) {
    this.currentClient = event.value;
    this.globalFilterService.selectedClientId = event.value;
    this.applyBannerLayoutOffset();
  }

  private injectHealthcareTopBar(): Promise<any> {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.async = true;
      script.src = environment.healthcareTopBar;
      script.addEventListener("load", resolve);
      script.addEventListener("error", () => reject("Error loading script."));
      script.addEventListener("abort", () => reject("Script loading aborted."));
      document.body.appendChild(script);
    });
  }

  checkValidLink(feature: string): boolean {
    if (!feature) {
      return false;
    }
    if (!this.authorizationService.hasFeature(feature)) {
      return false;
    }
    return true;
  }

  PHIToggleCallback(phiValue?: string) {
    // console.log(`PHIToggleCallBack - ${phiValue}`)
    if (phiValue) {
      this.phiService.PhiToggle = true;
    } else {
      this.phiService.PhiToggle = false;
    }
  }

  /**
   * Applies layout offsets for client-specific banners (3010, 3020).
   * Adjusts top bar, main content, and navbar margins when banner is displayed.
   * Triggers a small scroll to force side nav repositioning after layout changes.
   */
  private applyBannerLayoutOffset(): void {
    const topbarElement = document.getElementById("healthcare-top-bar");
    const mainContentElement = document.querySelector(".inm-layout-main") as HTMLElement;
    const sideNav = document.querySelector(".inm-expandable--sidebar") as HTMLElement;
    const portalNavbar = document.getElementById('portal-navbar');
    if (this.currentClient === 3010 || this.currentClient === 3020) {
      this.displayBanner = true;
      if (topbarElement && mainContentElement) {
        topbarElement.style.setProperty("margin-top", "50px", "important");
        mainContentElement.style.setProperty("margin-top", "117px", "important");
        portalNavbar.style.setProperty("height", "117px", "important");
      }
    } else {
      this.displayBanner = false;
      if (topbarElement && mainContentElement) {
        topbarElement.style.removeProperty("margin-top");
        mainContentElement.style.removeProperty("margin-top");
        portalNavbar.style.removeProperty("height");
        if (sideNav) sideNav.style.removeProperty("top");
      }
    }

    // Wait for Angular change detection to complete and DOM to update
    setTimeout(() => {
      // Trigger scroll to force side nav repositioning
      const x = window.scrollX;
      const y = window.scrollY;

      // ensure page can scroll
      if (document.body.scrollHeight > window.innerHeight) {
        requestAnimationFrame(() => {
          window.scrollTo(x, y + 0.5);         // small invisible move
        });
      }
    }, 10);
  }

  /**
   * Handles scroll events to show/hide the sticky popup banner.
   * When user scrolls past the client banner, displays a sticky version at the top.
   */
  private onScroll(): void {
    if (!this.displayBanner) {
      this.showStickyPopup = false;
      this.adjustSideNavPosition(false);
      return;
    }

    const banner = document.querySelector('.client-banner') as HTMLElement;
    if (banner) {
      const bannerBottom = banner.offsetTop + banner.offsetHeight;
      // Show popup slightly before banner fully scrolls out (20px before the end)
      const shouldShowPopup = window.scrollY > (bannerBottom - 20);
      this.showStickyPopup = shouldShowPopup;
      this.adjustSideNavPosition(shouldShowPopup);
    }
  }

  /**
   * Adjusts the side navigation vertical position based on sticky popup visibility.
   * When popup is visible and navbar is scrolled up, positions side nav below the popup.
   * @param popupVisible - Whether the sticky popup banner is currently visible
   */
  private adjustSideNavPosition(popupVisible: boolean): void {
    const sideNav = document.querySelector('.inm-expandable--sidebar') as HTMLElement;
    const portalNavbar = document.getElementById('portal-navbar');

    if (sideNav && portalNavbar) {
      const computedStyle = window.getComputedStyle(portalNavbar);
      const navbarTop = parseInt(computedStyle.top) || 0;

      if (popupVisible && navbarTop < -100) {
        // Get the actual height of the popup banner
        const popup = document.querySelector('.client-banner-popup') as HTMLElement;
        const popupHeight = popup ? popup.offsetHeight : 40; // fallback to 40px if not found
        sideNav.style.setProperty('top', `${popupHeight}px`, 'important');
      }
    }
  }

  /**
   * Gets the configuration for the current client's banner display.
   * Returns client name and color scheme for clients 3010 and 3020.
   * @returns Object containing client name, background color, and foreground color
   */
  getClientConfig() {
    const client = this.usersClients.find(c => c.value === this.currentClient);
    const clientName = client ? client.label.split(' - ')[1].toUpperCase() : '';

    switch (this.currentClient) {
      case 3010:
        return {
          name: clientName,
          background: '#003288',
          foreground: '#FFFFFF'
        };
      case 3020:
        return {
          name: clientName,
          background: '#FFC220',
          foreground: '#000000'
        };
    }
  }
}
